import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Heading, Text } from '../ui'
import { AppointmentAttachmentsList } from './AppointmentAttachmentsList'
import { supportCenterAttendService } from '../../services/supportCenterAttend'
import type { SupportCenterTriageListItem } from '../../types/supportCenterTriage'
import type { SupportCenterAppointmentStatus } from '../../types/supportCenterTriage'
import {
  TRIAGE_DEMAND_NATURE_OPTIONS,
  TRIAGE_RISK_OPTIONS,
  TRIAGE_SUFFERING_OPTIONS,
  formatBirthYearAndAge,
  attendanceNotesLabel,
  isReducedTriageForm,
  triageMainReasonLabel,
} from '../../types/supportCenterTriage'
import { APPOINTMENT_STATUS_LABELS } from '../../types/supportCenterReports'
import { appointmentChannelLabel } from '../../types/supportCenterAppointments'
import {
  appointmentFormKind,
  resolveAssistanceFormKind,
  type SupportCenterFormKind,
} from '../../types/supportCenterSpecialties'
import {
  SUPPORT_CENTER_PSYCH_VIEW_FIELDS,
  type SupportCenterAttendanceNotesRecord,
  type SupportCenterPsychAttendanceRecord,
} from '../../types/supportCenterPsychAttendance'
import { formatPhone, patientKinshipLabel } from './dashboardHelpers'

const fieldClass =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm min-h-[2.5rem] whitespace-pre-wrap'

type DetailTab = 'triagem' | 'atendimento'

export type TriagemDetailAppointmentHint = {
  appointment_id?: string | null
  specialization_name?: string | null
  appointment_status?: SupportCenterAppointmentStatus | null
  triage_status_label?: string | null
  scheduled_at?: string | null
  /** Kind do formulário da assistência (fallback quando o embed da triagem não traz). */
  form_kind?: SupportCenterFormKind | null
}

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '')
}

function formatCpf(value: string): string {
  const digits = onlyDigits(value).slice(0, 11)
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

function formatDateTime(value: string): string {
  if (!value) return '—'
  return new Date(value).toLocaleString('pt-BR')
}

function formatDateBr(isoDate: string): string {
  if (!/^\d{4}-\d{2}-\d{2}/.test(isoDate)) return isoDate
  const [y, m, d] = isoDate.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

function labelFromOptions(
  value: string | null | undefined,
  options: Array<{ value: string; label: string }>
): string {
  if (!value) return '—'
  return options.find((item) => item.value === value)?.label ?? value
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      <div className={fieldClass}>{value || '—'}</div>
    </div>
  )
}

function renderDemandList(
  items:
    | Array<{
        id: string
        name: string
        notes?: string | null
      }>
    | null
    | undefined
): React.ReactNode {
  const rows = (items ?? []).filter((item) => item.name?.trim())
  if (rows.length === 0) return '—'
  return (
    <div className="space-y-1">
      {rows.map((item) => (
        <div key={item.id}>
          {item.name}
          {item.notes?.trim() ? `: ${item.notes.trim()}` : ''}
        </div>
      ))}
    </div>
  )
}

type TriagemDetailModalProps = {
  triage: SupportCenterTriageListItem | null
  appointmentHint?: TriagemDetailAppointmentHint | null
  canUpdate?: boolean
  onClose: () => void
}

export const TriagemDetailModal: React.FC<TriagemDetailModalProps> = ({
  triage,
  appointmentHint,
  canUpdate = false,
  onClose,
}) => {
  const [activeTab, setActiveTab] = React.useState<DetailTab>('triagem')
  const [attendanceNotes, setAttendanceNotes] =
    React.useState<SupportCenterAttendanceNotesRecord | null>(null)
  const [loadingNotes, setLoadingNotes] = React.useState(false)
  const [notesError, setNotesError] = React.useState<string | null>(null)
  const [psychAttendance, setPsychAttendance] =
    React.useState<SupportCenterPsychAttendanceRecord | null>(null)
  const [loadingPsych, setLoadingPsych] = React.useState(false)
  const [psychError, setPsychError] = React.useState<string | null>(null)

  React.useEffect(() => {
    setActiveTab('triagem')
  }, [triage?.id, appointmentHint?.scheduled_at])

  const formKind =
    appointmentFormKind(triage?.appointment) ??
    resolveAssistanceFormKind(triage?.form_key) ??
    appointmentHint?.form_kind ??
    null
  const isPsychological = formKind === 'psicologico'
  const isReducedForm = isReducedTriageForm(formKind)
  const notesLabel = attendanceNotesLabel(formKind)
  const holderCpf = triage?.appointment?.holder_cpf ?? ''
  const patientCpf = triage?.appointment?.patient_cpf ?? ''
  const patientPeople = triage?.appointment?.patient_people ?? null
  const patientName = patientPeople?.name ?? null
  const patientIsInsured =
    Boolean(onlyDigits(holderCpf) && onlyDigits(holderCpf) === onlyDigits(patientCpf))
  const kinshipLabel = triage
    ? patientKinshipLabel({
        holder_cpf: holderCpf,
        patient_cpf: patientCpf,
        relationship_to_holder: triage.appointment?.relationship_to_holder,
      })
    : null
  const triageDemandList = renderDemandList(triage?.demand_types)
  const attendanceDemandList = renderDemandList(
    psychAttendance?.demand_types ?? attendanceNotes?.demand_types
  )
  const psy = triage?.psychological

  const specializationName =
    triage?.appointment?.specialization?.name ||
    appointmentHint?.specialization_name ||
    'Especialidade'
  const appointmentId =
    appointmentHint?.appointment_id || triage?.appointment?.id || null

  React.useEffect(() => {
    if (!appointmentId) {
      setAttendanceNotes(null)
      setPsychAttendance(null)
      setLoadingNotes(false)
      setLoadingPsych(false)
      setNotesError(null)
      setPsychError(null)
      return
    }
    if (formKind === 'psicologico') {
      let cancelled = false
      setAttendanceNotes(null)
      setNotesError(null)
      setLoadingNotes(false)
      setLoadingPsych(true)
      setPsychError(null)
      void supportCenterAttendService
        .getPsychologicalAttendance(appointmentId)
        .then((row) => {
          if (!cancelled) setPsychAttendance(row)
        })
        .catch((e) => {
          if (!cancelled) {
            setPsychAttendance(null)
            setPsychError(
              e instanceof Error ? e.message : 'Não foi possível carregar o prontuário.'
            )
          }
        })
        .finally(() => {
          if (!cancelled) setLoadingPsych(false)
        })
      return () => {
        cancelled = true
      }
    }
    if (!isReducedTriageForm(formKind)) {
      setAttendanceNotes(null)
      setPsychAttendance(null)
      setLoadingNotes(false)
      setLoadingPsych(false)
      setNotesError(null)
      setPsychError(null)
      return
    }
    const kind = formKind
    let cancelled = false
    setPsychAttendance(null)
    setLoadingPsych(false)
    setPsychError(null)
    setLoadingNotes(true)
    setNotesError(null)
    void supportCenterAttendService
      .getAttendanceNotes(kind, appointmentId)
      .then((row) => {
        if (!cancelled) setAttendanceNotes(row)
      })
      .catch((e) => {
        if (!cancelled) {
          setAttendanceNotes(null)
          setNotesError(
            e instanceof Error ? e.message : 'Não foi possível carregar o atendimento.'
          )
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingNotes(false)
      })
    return () => {
      cancelled = true
    }
  }, [appointmentId, formKind])

  const tabClass = (tab: DetailTab) =>
    `py-3 px-1 border-b-2 font-medium text-sm ${
      activeTab === tab
        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="triagem-detail-modal-title"
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="min-w-0">
            <Heading
              as="h2"
              size="2xl"
              id="triagem-detail-modal-title"
              className="text-gray-900 dark:text-white"
            >
              Detalhe
            </Heading>
          </div>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </div>

        <div className="px-6 pt-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <nav className="-mb-px flex space-x-8">
            <button type="button" onClick={() => setActiveTab('triagem')} className={tabClass('triagem')}>
              Triagem
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('atendimento')}
              className={tabClass('atendimento')}
            >
              Atendimento
            </button>
          </nav>
        </div>

        <div className="px-6 py-5 overflow-y-auto space-y-6 flex-1 min-h-0">
          {activeTab === 'triagem' && (
            <div className="space-y-6">
              {!triage ? (
                <div className="py-10 text-center">
                  <Text size="sm" color="muted">
                    Sem triagem vinculada a este atendimento.
                  </Text>
                </div>
              ) : (
                <>
                  <section className="space-y-3">
                    <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                      Validação
                    </Heading>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <DetailField
                        label="Nome do segurado"
                        value={triage.appointment?.holder_people?.name}
                      />
                      <DetailField label="CPF do segurado" value={formatCpf(holderCpf)} />
                      <DetailField label="Nome do paciente" value={patientName} />
                      <DetailField label="CPF do paciente" value={formatCpf(patientCpf)} />
                      <DetailField
                        label="Paciente é o segurado"
                        value={patientIsInsured ? 'Sim' : 'Não'}
                      />
                      <DetailField label="Parentesco" value={kinshipLabel} />
                      <DetailField
                        label="Telefone"
                        value={formatPhone(triage.appointment?.patient_phone)}
                      />
                    </div>
                  </section>

                  <section className="space-y-3">
                    <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                      Cabeçalho
                    </Heading>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <DetailField
                        label="Data da triagem"
                        value={
                          triage.triage_date
                            ? formatDateBr(triage.triage_date)
                            : triage.triaged_at
                              ? formatDateTime(triage.triaged_at)
                              : null
                        }
                      />
                      <DetailField label="Responsável" value={triage.intern?.name} />
                    </div>
                  </section>

                  <section className="space-y-3">
                    <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                      Dados do paciente
                    </Heading>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <DetailField
                        label="Nascimento / idade"
                        value={formatBirthYearAndAge(
                          patientPeople?.captured_birth_date,
                          triage.triage_date
                        )}
                      />
                      {isPsychological && (
                        <>
                      <DetailField
                        label="Contato de emergência"
                        value={formatPhone(triage.emergency_contact_phone)}
                      />
                      <DetailField
                        label="Parentesco do contato"
                        value={triage.emergency_contact_relation}
                      />
                        </>
                      )}
                    </div>
                  </section>

                  <section className="space-y-3">
                    <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                      Dados do atendimento
                    </Heading>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <DetailField
                        label="Origem do encaminhamento"
                        value={
                          triage.referral_origin?.name
                            ? triage.referral_origin_other
                              ? `${triage.referral_origin.name}: ${triage.referral_origin_other}`
                              : triage.referral_origin.name
                            : triage.referral_origin_other
                        }
                      />
                      {isPsychological && (
                        <DetailField label="Disponibilidade" value={triage.availability} />
                      )}
                    </div>
                    <DetailField
                      label={triageMainReasonLabel(formKind)}
                      value={
                        formKind === 'juridico'
                          ? triage.juridical?.activation_reason
                          : formKind === 'social'
                            ? triage.social?.activation_reason
                          : formKind === 'nutricional'
                            ? triage.nutritional?.assistance_objective
                            : formKind === 'fitness'
                              ? triage.fitness?.assistance_objective
                              : triage.main_reason
                      }
                    />
                  </section>

                  <section className="space-y-3">
                    <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                      Tipo de demanda percebida
                    </Heading>
                    <DetailField label="Demandas" value={triageDemandList} />
                  </section>

                  {isPsychological && (
                    <section className="space-y-3">
                      <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                        Avaliação psicológica
                      </Heading>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <DetailField
                          label="Intensidade do sofrimento"
                          value={labelFromOptions(psy?.suffering_intensity, TRIAGE_SUFFERING_OPTIONS)}
                        />
                        <DetailField
                          label="Natureza da demanda"
                          value={labelFromOptions(psy?.demand_nature, TRIAGE_DEMAND_NATURE_OPTIONS)}
                        />
                        <DetailField
                          label="Ideação suicida"
                          value={labelFromOptions(psy?.suicidal_ideation, TRIAGE_RISK_OPTIONS)}
                        />
                        <DetailField
                          label="Autoagressão"
                          value={labelFromOptions(psy?.self_harm, TRIAGE_RISK_OPTIONS)}
                        />
                        <DetailField
                          label="Risco de agressão"
                          value={labelFromOptions(psy?.aggression_risk, TRIAGE_RISK_OPTIONS)}
                        />
                        <DetailField
                          label="Violência doméstica / abuso"
                          value={labelFromOptions(psy?.domestic_violence, TRIAGE_RISK_OPTIONS)}
                        />
                      </div>
                      <DetailField label="Observações" value={psy?.observations} />
                    </section>
                  )}

                  {(triage.status === 'reprovado' || triage.status === 'cancelado') && (
                    <section className="space-y-3">
                      <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                        {triage.status === 'cancelado' ? 'Cancelamento' : 'Reprovação'}
                      </Heading>
                      {triage.appointment?.cancellation_channel ? (
                        <DetailField
                          label="Canal do cancelamento"
                          value={appointmentChannelLabel(triage.appointment.cancellation_channel)}
                        />
                      ) : null}
                      <DetailField
                        label="Motivo"
                        value={triage.cancel_reason?.name}
                      />
                      {triage.cancel_reason?.description ? (
                        <DetailField label="Descrição" value={triage.cancel_reason.description} />
                      ) : null}
                    </section>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'atendimento' && (
            <div className="space-y-6">
              <section className="space-y-3">
                <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                  Dados do atendimento
                </Heading>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <DetailField
                    label="Consulta"
                    value={
                      appointmentHint?.scheduled_at
                        ? formatDateTime(appointmentHint.scheduled_at)
                        : triage?.appointment?.scheduled_at
                          ? formatDateTime(triage.appointment.scheduled_at)
                          : null
                    }
                  />
                  <DetailField
                    label="Status"
                    value={
                      appointmentHint?.appointment_status
                        ? APPOINTMENT_STATUS_LABELS[appointmentHint.appointment_status]
                        : triage?.appointment?.status
                          ? APPOINTMENT_STATUS_LABELS[triage.appointment.status]
                          : null
                    }
                  />
                  <DetailField label="Especialidade" value={specializationName} />
                  <DetailField
                    label="Profissional"
                    value={triage?.appointment?.professional?.name}
                  />
                </div>
              </section>

              {isPsychological && (
                <>
                  {loadingPsych ? (
                    <Text size="sm" color="muted">
                      Carregando prontuário...
                    </Text>
                  ) : psychError ? (
                    <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
                      {psychError}
                    </div>
                  ) : psychAttendance ? (
                    <div className="space-y-3">
                      <DetailField label="Tipo de demanda" value={attendanceDemandList} />
                      <DetailField
                        label="Medicamentos"
                        value={
                          psychAttendance.medications.length > 0
                            ? psychAttendance.medications.join('\n')
                            : null
                        }
                      />
                      {SUPPORT_CENTER_PSYCH_VIEW_FIELDS.map((field) => {
                        const raw = psychAttendance[field.key]
                        const value =
                          field.key === 'next_contact_date' && raw
                            ? formatDateBr(raw)
                            : raw
                        return <DetailField key={field.key} label={field.label} value={value} />
                      })}
                    </div>
                  ) : (
                    <Text size="sm" color="muted">
                      Nenhum prontuário gravado para este atendimento.
                    </Text>
                  )}
                </>
              )}

              {isReducedForm && (
                <>
                  {loadingNotes ? (
                    <Text size="sm" color="muted">
                      Carregando atendimento...
                    </Text>
                  ) : notesError ? (
                    <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
                      {notesError}
                    </div>
                  ) : attendanceNotes &&
                    (attendanceNotes.notes?.trim() ||
                      attendanceDemandList !== '—') ? (
                    <div className="space-y-3">
                      <DetailField label="Tipo de demanda" value={attendanceDemandList} />
                      <DetailField label={notesLabel} value={attendanceNotes.notes} />
                    </div>
                  ) : (
                    <Text size="sm" color="muted">
                      Nenhum dado de atendimento gravado
                      {notesLabel ? ` (${notesLabel.toLowerCase()})` : ''}.
                    </Text>
                  )}
                </>
              )}

              <AppointmentAttachmentsList
                appointmentId={appointmentId}
                variant="section"
                title="Anexos"
                canUpload={!!canUpdate}
              />

              {!appointmentId && (
                <div className="py-6 text-center">
                  <Text size="sm" color="muted">
                    Nenhum atendimento vinculado para exibir anexos.
                  </Text>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <Button variant="solid" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}
